import { Injectable, Injector } from '@angular/core';
import {
  HttpEvent, HttpInterceptor, HttpHandler, HttpRequest, HttpBackend, HttpClient, HttpErrorResponse
} from '@angular/common/http';
import { Observable, throwError, of, from } from 'rxjs';
import { AuthService } from '../services/auth.service';
import Swal from 'sweetalert2/dist/sweetalert2.js';
import { environment } from '../../environments/environment';
import { switchMap, catchError, map } from 'rxjs/operators';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private bypassHttp: HttpClient;

  constructor(private authService: AuthService, private httpBackend: HttpBackend, private injector: Injector) {
    // Create a HttpClient instance that bypasses interceptors
    this.bypassHttp = new HttpClient(this.httpBackend);
  }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // If session already marked invalid by polling, block immediately
    try {
      if (this.authService && (this.authService as any).sessionInvalidated) {
        return throwError(() => new HttpErrorResponse({ status: 440, statusText: 'Sessão expirada' }));
      }
    } catch (e) {}

    const isControlAppApi = req.url.startsWith(environment.controllApp);
    const isCedaeApi = req.url.startsWith(environment.cedaeapp);
    const isOurApi = isControlAppApi || isCedaeApi;
    const isValidateEndpoint = req.url.includes('/usuario/validate-token');

    const token = this.authService.getToken ? this.authService.getToken() : null;
    const authorizedReq = token
      ? req.clone({ setHeaders: { Authorization: `Bearer ${token}` } })
      : req;

    const proceedWithRequest = () => {
      // Original permission checks remain
      if (isControlAppApi) {
        // Bloquear exclusão de usuários para não-SuperAdmins
        if (req.method === 'DELETE' && req.url.includes('/usuario/delete/')) {
          if (!this.authService.canPerformOperation('deleteUser')) {
            Swal.fire({
              title: 'Permissão Negada',
              text: 'Somente SuperAdministrador pode excluir usuários.',
              icon: 'error',
              confirmButtonText: 'OK'
            });
            return throwError(() => new Error('Permissão negada para excluir usuários'));
          }
        }

        // Bloquear registro de usuários para não-SuperAdmins
        if (req.method === 'POST' && req.url.includes('/usuario/register')) {
          if (!this.authService.canPerformOperation('registerUser')) {
            Swal.fire({
              title: 'Permissão Negada',
              text: 'Somente SuperAdministrador pode cadastrar usuários.',
              icon: 'error',
              confirmButtonText: 'OK'
            });
            return throwError(() => new Error('Permissão negada para cadastrar usuários'));
          }
        }

        // Bloquear atualização de papel de usuário para não-SuperAdmins
        if (req.method === 'PUT' && req.url.includes('/usuario/update/')) {
          const formData = req.body;
          if (formData instanceof FormData && formData.has('role')) {
            if (!this.authService.canPerformOperation('updateUserRole')) {
              Swal.fire({
                title: 'Permissão Negada',
                text: 'Somente SuperAdministrador pode alterar funções de usuários.',
                icon: 'error',
                confirmButtonText: 'OK'
              });
              return throwError(() => new Error('Permissão negada para alterar funções'));
            }
          }
        }
      }

      // Verificar exclusão de consumidor
      if (req.url.includes(`${environment.cedaeapp}/exclusao/excluirporjuncao/`)) {
        if (req.method === 'DELETE') {
          if (!this.authService.isSuperAdmin()) {
            Swal.fire({
              title: 'Permissão Negada',
              text: 'Somente SuperAdministrador pode excluir consumidores.',
              icon: 'error',
              confirmButtonText: 'OK'
            });
            return throwError(() => new Error('Permissão negada para excluir consumidores'));
          }
        }
      }

      return next.handle(authorizedReq);
    };

    // If not our API or it's the validate endpoint itself, just proceed
    if (!isOurApi || isValidateEndpoint || !token) {
      return proceedWithRequest() as Observable<HttpEvent<any>>;
    }

    // Validate token via bypassing HTTP client to avoid interceptor recursion
    const usuario = this.authService.getUsuario ? this.authService.getUsuario() : null;
    const usuarioId = usuario?.usuarioId || JSON.parse(localStorage.getItem('usuario') || '{}')?.usuarioId;

    return this.bypassHttp.post<any>(`${environment.controllApp}/usuario/validate-token`, { token, userId: usuarioId }, {
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` }
    }).pipe(
      map(resp => resp === true || resp === 'true'),
      switchMap((isValid: boolean) => {
        if (!isValid) {
          // Create a HttpErrorResponse with status 440 so ErrorInterceptor handles it
          return throwError(() => new HttpErrorResponse({ status: 440, statusText: 'Sessão expirada' }));
        }
        // proceedWithRequest may return Observable<HttpEvent<any>> or a thrown error
        return from(proceedWithRequest() as Observable<HttpEvent<any>>);
      }),
      catchError(err => {
        // If validation call failed (network), allow original request to proceed
        console.warn('Validação de token falhou, permitindo requisição: ', err);
        return from(proceedWithRequest() as Observable<HttpEvent<any>>);
      })
    ) as Observable<HttpEvent<any>>;
  }
}
